/**
 * Library-first 3D model add for Grid AI automation.
 * 1) Best match from SpatialGrid library
 * 2) Fallback to Sketchfab
 *
 * Skips models already in the grid for every add.
 * Re-adding the same asset is only allowed when the user asks for it
 * (e.g. "again" / "same" / exact existing title).
 */

import toast from 'react-hot-toast';
import { handleLibraryAssetImport } from '../../components/gridpagefunction/LibraryAsset';
import {
  findBestLibraryModel,
  sanitizeDisplayName,
  isGenericModelQuery,
  getVarietyQueries,
} from './librarySearchService';
import { normalizeModelSearchQuery } from './modelRelevance';
import { sketchfabService } from './sketchfabService';
import { setLoadingMessage } from '../../redux/Slice/globalStates';
import store from '../../redux/store';

/**
 * Collect library / Sketchfab ids and display names already present in the grid.
 */
export function collectExistingModelIds(scene, reduxStore) {
  const libraryIds = new Set();
  const sketchfabUids = new Set();
  const displayNames = new Set();
  const entries = []; // { libraryId?, sketchfabUid?, name }
  const seenKeys = new Set();

  const remember = (obj) => {
    if (!obj || typeof obj !== 'object') return;
    const libId = obj.libraryAssetId || obj.userData?.libraryAssetId || null;
    // Only treat as library id when marked as library (avoid confusing temp ids)
    const resolvedLibId =
      libId ||
      (obj.source === 'library' && obj.libraryAssetId
        ? obj.libraryAssetId
        : null);
    if (resolvedLibId) libraryIds.add(String(resolvedLibId));

    const uid =
      obj.sketchfabUid ||
      obj.userData?.sketchfabUid ||
      extractSketchfabUid(obj);
    if (uid) sketchfabUids.add(String(uid));

    const name = String(
      obj.file_name ||
        obj.userData?.displayName ||
        obj.asset_file_name ||
        ''
    ).trim();
    if (name) displayNames.add(name);

    const isMesh =
      obj.deleteType === 'mesh' ||
      obj.objType === '3d' ||
      obj.source === 'library' ||
      obj.source === 'sketchfab' ||
      obj.userData?.type === 'sketchfab' ||
      obj.userData?.originalType === 'sketchfab_model' ||
      !!resolvedLibId ||
      !!uid;
    if (!isMesh) return;

    const key =
      (resolvedLibId && `lib:${resolvedLibId}`) ||
      (uid && `sf:${uid}`) ||
      (obj.name && `n:${obj.name}`) ||
      (obj._id && `id:${obj._id}`) ||
      null;
    if (!key || seenKeys.has(key)) return;
    seenKeys.add(key);
    entries.push({
      libraryId: resolvedLibId ? String(resolvedLibId) : null,
      sketchfabUid: uid ? String(uid) : null,
      name,
    });
  };

  const assets = reduxStore?.getState?.()?.assets_object || {};
  (assets.sceneMeshes || []).forEach(remember);
  (assets.assets_data?.mesh || []).forEach(remember);

  if (scene?.traverse) {
    scene.traverse((child) => {
      if (!child?.isObject3D) return;
      if (
        child.deleteType === 'mesh' ||
        child.objType === '3d' ||
        child.source === 'library' ||
        child.libraryAssetId ||
        child.userData?.type === 'sketchfab' ||
        child.userData?.originalType === 'sketchfab_model'
      ) {
        remember(child);
      }
    });
  }

  return {
    libraryIds,
    sketchfabUids,
    displayNames,
    entries,
    meshCount: seenKeys.size || libraryIds.size + sketchfabUids.size,
  };
}

/**
 * True when the user asks to re-add a model already in the grid
 * (e.g. "same car again", or the exact display name of an existing asset).
 */
export function wantsSameModelAgain(query, existing = {}) {
  const qRaw = String(query || '').trim();
  const q = qRaw.toLowerCase();
  if (!q) return { allow: false };

  if (
    /\b(same(\s+(one|model|glb|gltf|asset))?|again|duplicate|another\s+copy|exact(ly)?(\s+same)?)\b/i.test(
      qRaw
    )
  ) {
    return { allow: true, reason: 'explicit' };
  }

  const names = [
    ...(existing.displayNames || []),
    ...((existing.entries || []).map((e) => e.name).filter(Boolean) || []),
  ];

  for (const name of names) {
    const n = String(name).trim();
    if (n.length < 3) continue;
    const nl = n.toLowerCase();

    // Quoted exact title: add "Red Sports Car"
    const quoted = qRaw.match(/["']([^"']{3,120})["']/);
    if (quoted && quoted[1].trim().toLowerCase() === nl) {
      const entry = (existing.entries || []).find(
        (e) => String(e.name || '').toLowerCase() === nl
      );
      return {
        allow: true,
        reason: 'exact_name',
        name: n,
        libraryId: entry?.libraryId || null,
        sketchfabUid: entry?.sketchfabUid || null,
      };
    }

    // Full title as the query (not a short category like "car")
    if (nl === q || q === `the ${nl}` || q === `a ${nl}` || q === `an ${nl}`) {
      if (
        isGenericModelQuery(q) ||
        (q.split(/\s+/).length < 2 && q.length < 8)
      ) {
        continue;
      }
      const entry = (existing.entries || []).find(
        (e) => String(e.name || '').toLowerCase() === nl
      );
      return {
        allow: true,
        reason: 'exact_name',
        name: n,
        libraryId: entry?.libraryId || null,
        sketchfabUid: entry?.sketchfabUid || null,
      };
    }
  }

  return { allow: false };
}

function extractSketchfabUid(obj) {
  const code = String(
    obj.userData?.mesh_code || obj.mesh_code || obj.asset_file_name || ''
  );
  const m = code.match(/sketchfab[_-]([a-zA-Z0-9]+)/i);
  if (m) return m[1];
  const name = String(obj.name || obj.temp_file_name || '');
  const m2 = name.match(/sketchfab[_-]([a-zA-Z0-9]+)/i);
  return m2 ? m2[1] : null;
}

/**
 * @param {string} query
 * @param {THREE.Scene} scene
 * @param {object} reduxStore
 * @param {{ position?: object, scale?: object, transformControls?: object, setPolyCount?: Function, setPendingUploads?: Function }} [options]
 */
export async function addModelFromLibraryOrSketchfab(
  query,
  scene,
  reduxStore,
  options = {}
) {
  const qRaw = String(query || '').trim();
  // Normalize "a car model" → "car" before searching
  const q = normalizeModelSearchQuery(qRaw) || qRaw;
  if (!q) {
    return { success: false, message: 'Search query is required' };
  }

  const transformControls =
    options.transformControls ||
    window.gridPageSceneObjects?.transformControls ||
    null;
  const setPolyCount =
    options.setPolyCount ||
    window.gridPageSceneObjects?.setPolyCount ||
    (() => {});
  const setPendingUploads = options.setPendingUploads || null;

  const existing = collectExistingModelIds(scene, reduxStore);
  const generic = isGenericModelQuery(q);
  const varietyOffset = existing.meshCount;
  const sameRequest = wantsSameModelAgain(q, existing);
  const allowSame = sameRequest.allow === true;

  // Always unique unless the user specifically asked for the same model again
  const excludeLibraryIds = allowSame
    ? [...existing.libraryIds].filter(
        (id) =>
          !(sameRequest.libraryId && String(id) === String(sameRequest.libraryId))
      )
    : [...existing.libraryIds];
  const excludeUids = allowSame
    ? [...existing.sketchfabUids].filter(
        (id) =>
          !(
            sameRequest.sketchfabUid &&
            String(id) === String(sameRequest.sketchfabUid)
          )
      )
    : [...existing.sketchfabUids];

  // ── 1. SpatialGrid library ──────────────────────────────────────────
  try {
    const best = await findBestLibraryModel(q, {
      type: '3D',
      limit: 24,
      excludeLibraryIds,
      allowLibraryIds: allowSame && sameRequest.libraryId
        ? [sameRequest.libraryId]
        : [],
      varietyOffset,
      requireUnique: !allowSame,
    });
    if (best?.item) {
      const asset = best.item;
      const displayName = sanitizeDisplayName(
        asset.name || asset.title,
        best.searchTerm || q
      );

      await handleLibraryAssetImport(
        { ...asset, name: displayName },
        { scene, transformControls, setPolyCount }
      );

      applyOptionalPose(scene, displayName, options);

      return {
        success: true,
        source: 'library',
        modelInfo: {
          name: displayName,
          id: asset._id,
          score: best.score,
          type: asset.type,
          searchTerm: best.searchTerm || q,
        },
        message: allowSame
          ? `Added another copy of "${displayName}" from library`
          : `Added "${displayName}" from SpatialGrid library`,
      };
    }
  } catch (libraryError) {
    console.warn(
      '[modelAdd] Library search/import failed, falling back to Sketchfab:',
      libraryError?.message || libraryError
    );
    toast.error('Library lookup failed — trying Sketchfab…');
  }

  // ── 2. Sketchfab fallback ───────────────────────────────────────────
  try {
    const result = await sketchfabService.searchAndAddModel(
      q,
      {
        scene,
        transformControls,
        selectedObject: null,
        setPolyCount,
      },
      setPendingUploads,
      {
        excludeUids,
        allowUids:
          allowSame && sameRequest.sketchfabUid
            ? [sameRequest.sketchfabUid]
            : [],
        varietyOffset,
        preferVariety: generic,
        requireUnique: !allowSame,
      }
    );

    if (result?.success) {
      applyOptionalPose(
        scene,
        result.modelInfo?.name || result.tempId,
        options,
        result.model
      );
      return {
        ...result,
        source: 'sketchfab',
        message:
          result.message ||
          `Added "${result.modelInfo?.name || q}" from Sketchfab`,
      };
    }

    return {
      success: false,
      source: 'sketchfab',
      message:
        result?.message ||
        (allowSame
          ? `Could not re-add model for "${q}"`
          : `No new unique model found for "${q}" — matching ones are already in the grid. Name a specific model to add a copy.`),
      error: result?.error,
    };
  } catch (sketchfabError) {
    console.error('[modelAdd] Sketchfab fallback failed:', sketchfabError);
    try {
      reduxStore?.dispatch?.(setLoadingMessage(false));
      store.dispatch(setLoadingMessage(false));
    } catch (e) {
      console.warn('[modelAdd] failed to clear loading state', e);
    }
    return {
      success: false,
      source: 'sketchfab',
      message: `Failed to add model for "${q}": ${sketchfabError.message}`,
      error: sketchfabError.message,
    };
  }
}

function applyOptionalPose(scene, nameHint, options, knownModel = null) {
  if (!options?.position && !options?.scale) return;

  let obj = knownModel;
  if (!obj && scene && nameHint) {
    scene.traverse((child) => {
      if (!child?.isObject3D) return;
      if (
        child.file_name === nameHint ||
        child.name === nameHint ||
        child.userData?.displayName === nameHint
      ) {
        obj = child;
      }
    });
  }
  if (!obj) return;

  if (options.position && obj.position) {
    const p = options.position;
    if (p.x != null) obj.position.x = Number(p.x);
    if (p.y != null) obj.position.y = Number(p.y);
    if (p.z != null) obj.position.z = Number(p.z);
  }
  if (options.scale && obj.scale) {
    const s = options.scale;
    if (typeof s === 'number') {
      obj.scale.set(s, s, s);
    } else {
      if (s.x != null) obj.scale.x = Number(s.x);
      if (s.y != null) obj.scale.y = Number(s.y);
      if (s.z != null) obj.scale.z = Number(s.z);
    }
  }
}

export { isGenericModelQuery, getVarietyQueries };
export default addModelFromLibraryOrSketchfab;
